import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedClient } from './queries'
import type { components } from './schema'

export type ShippingPolicy = components['schemas']['ShippingPolicyVersion']
export type ShippingPolicyUpdate = components['schemas']['PolicyUpdate']
export type ShippingOrigin = components['schemas']['ShippingOrigin']
export type ShippingOriginWrite = components['schemas']['OriginWrite']
export type ShippingOverrideWrite = components['schemas']['OverrideWrite']
export type ShippingReadiness = components['schemas']['ReadinessReport']
export type ShippingRateCard = components['schemas']['ShippingRateCardVersion']
export type ShippingRateCardCreate = components['schemas']['RateCardCreate']
export type ShippingQuoteAttemptSummary = components['schemas']['ShippingQuoteAttemptSummary']
export type ShippingQuoteAttemptDetail = components['schemas']['ShippingQuoteAttemptDetail']
export type ShippingSimulatorRequest = components['schemas']['SimulatorRequest']

const all = ['shipping-calculator'] as const

export function useShippingPolicies() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...all, 'policies'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipping-calculator/policies')
      if (error) throw error
      return data
    },
    refetchInterval: 5_000,
  })
}

export function useShippingReadiness(policyId?: number | null) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...all, 'readiness', policyId],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipping-calculator/readiness', {
        params: { query: { policy_id: policyId ?? undefined } },
      })
      if (error) throw error
      return data
    },
    enabled: policyId != null,
    refetchInterval: 5_000,
  })
}

function useShippingMutation<TInput, TOutput>(
  mutationFn: (input: TInput) => Promise<TOutput>,
) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: all }),
  })
}

export function useCreateShippingPolicyDraft() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (input: { sourcePolicyId?: number; name?: string }) => {
    const client = await getClient()
    const { data, error } = await client.POST('/dashboard/shipping-calculator/policies', {
      body: { source_policy_id: input.sourcePolicyId, name: input.name },
    })
    if (error) throw error
    return data
  })
}

export function useUpdateShippingPolicy() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(
    async (input: { policyId: number; body: ShippingPolicyUpdate }) => {
      const client = await getClient()
      const { data, error } = await client.PATCH(
        '/dashboard/shipping-calculator/policies/{policy_id}',
        {
          params: { path: { policy_id: input.policyId } },
          body: input.body,
        },
      )
      if (error) throw error
      return data
    },
  )
}

export function usePublishShippingPolicy() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (policyId: number) => {
    const client = await getClient()
    const { data, error } = await client.POST(
      '/dashboard/shipping-calculator/policies/{policy_id}/publish',
      { params: { path: { policy_id: policyId } } },
    )
    if (error) throw error
    return data
  })
}

export function useRollbackShippingPolicy() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (policyId: number) => {
    const client = await getClient()
    const { data, error } = await client.POST(
      '/dashboard/shipping-calculator/policies/{policy_id}/rollback',
      { params: { path: { policy_id: policyId } } },
    )
    if (error) throw error
    return data
  })
}

export function useRebuildShippingEstimates() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (policyId: number) => {
    const client = await getClient()
    const { data, error } = await client.POST(
      '/dashboard/shipping-calculator/policies/{policy_id}/estimate-snapshot/rebuild',
      { params: { path: { policy_id: policyId } } },
    )
    if (error) throw error
    return data
  })
}

export function useShippingOrigins() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...all, 'origins'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipping-calculator/origins', {
        params: { query: { active_only: true } },
      })
      if (error) throw error
      return data
    },
  })
}

export function useNormalizeShippingOrigins() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async () => {
    const client = await getClient()
    const { data, error } = await client.POST(
      '/dashboard/shipping-calculator/origins/normalize',
    )
    if (error) throw error
    return data
  })
}

export function useCreateShippingOrigin() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (body: ShippingOriginWrite) => {
    const client = await getClient()
    const { data, error } = await client.POST('/dashboard/shipping-calculator/origins', {
      body,
    })
    if (error) throw error
    return data
  })
}

export function useUpsertShippingOverride() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(
    async (input: { productId: number; body: ShippingOverrideWrite }) => {
      const client = await getClient()
      const { data, error } = await client.PUT(
        '/dashboard/shipping-calculator/products/{product_id}/override',
        {
          params: { path: { product_id: input.productId } },
          body: input.body,
        },
      )
      if (error) throw error
      return data
    },
  )
}

export function useShippingRateCards() {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...all, 'rate-cards'],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipping-calculator/rate-cards')
      if (error) throw error
      return data
    },
  })
}

export function useCreateShippingRateCard() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (body: ShippingRateCardCreate) => {
    const client = await getClient()
    const { data, error } = await client.POST('/dashboard/shipping-calculator/rate-cards', {
      body,
    })
    if (error) throw error
    return data
  })
}

export function useRebuildShippingRateCard() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (rateCardId: number) => {
    const client = await getClient()
    const { data, error } = await client.POST(
      '/dashboard/shipping-calculator/rate-cards/{rate_card_id}/rebuild',
      { params: { path: { rate_card_id: rateCardId } } },
    )
    if (error) throw error
    return data
  })
}

export function usePublishShippingRateCard() {
  const getClient = useAuthenticatedClient()
  return useShippingMutation(async (rateCardId: number) => {
    const client = await getClient()
    const { data, error } = await client.POST(
      '/dashboard/shipping-calculator/rate-cards/{rate_card_id}/publish',
      { params: { path: { rate_card_id: rateCardId } } },
    )
    if (error) throw error
    return data
  })
}

export function useShippingQuoteAttempts(limit = 100) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...all, 'attempts', limit],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/dashboard/shipping-calculator/quote-attempts', {
        params: { query: { limit } },
      })
      if (error) throw error
      return data
    },
    refetchInterval: 15_000,
  })
}

export function useShippingQuoteAttempt(attemptId: number) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: [...all, 'attempts', attemptId],
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET(
        '/dashboard/shipping-calculator/quote-attempts/{attempt_id}',
        { params: { path: { attempt_id: attemptId } } },
      )
      if (error) throw error
      return data
    },
    enabled: Number.isInteger(attemptId) && attemptId > 0,
  })
}

export function useSimulateShippingQuote() {
  const getClient = useAuthenticatedClient()
  return useMutation({
    mutationFn: async (body: ShippingSimulatorRequest) => {
      const client = await getClient()
      const { data, error } = await client.POST('/dashboard/shipping-calculator/simulate', {
        body,
      })
      if (error) throw error
      return data
    },
  })
}
